import { type GraphQLResponse, Observable } from 'relay-runtime';
import type { Sink } from 'relay-runtime/lib/network/RelayObservable';

/**
 * Server-injected GraphQL response cache.
 *
 * The Volt controller (or any other server-side query executor) emits inline
 * `<script>` tags that push GraphQL responses onto a global preload queue.
 * This module owns that queue and turns it into a lookup the Relay
 * environment's network layer consults before doing any HTTP fetch.
 *
 * Each cache entry is a buffered Observable + externally-pullable Sink, so
 * the primitive supports late producers (consumer subscribes before producer
 * pushes data) and streaming responses (multi-chunk @stream/@defer) — not
 * just the single-shot bootstrap case used today.
 *
 * Server-side script tag shape:
 *   <script>
 *     (globalThis.__TOWNSQUARE_RELAY_PRELOAD_QUEUE__ ||= []).push({
 *       operationKey: "<cacheID>{...stable variables JSON}",
 *       response: { data: { ... } }
 *     });
 *   </script>
 *
 * `operationKey` must match `getRequestIdentifier(request, variables)` from
 * `relay-runtime` —
 *   `(request.cacheID ?? request.id) + JSON.stringify(stableCopy(variables))`.
 */

/**
 * Each cache entry replays all buffered events to every new subscriber AND
 * broadcasts any further events to the set of currently-subscribed sinks.
 *
 * - Replay means: a consumer that subscribes after the producer completed
 *   still gets the full response (the SSR-handoff happy path).
 * - Multicast means: any number of consumers (Relay's `executeDeduped` probe
 *   plus the React hook plus other Relay envs sharded by cloudId) all see
 *   the same response — fixes the single-`capturedSink` bug where a second
 *   subscribe would orphan the first.
 *
 * The Sink stays a single-writer API. The producer doesn't know or care how
 * many consumers attach.
 */
type BufferedEvent =
	| { kind: 'next'; value: GraphQLResponse }
	| { kind: 'error'; error: Error }
	| { kind: 'complete' };

type CacheValue = {
	buffered: BufferedEvent[];
	closed: boolean;
	liveSubscribers: Set<Sink<GraphQLResponse>>;
	sink: Sink<GraphQLResponse>;
	observable: Observable<GraphQLResponse>;
};

class RelayPreloadedResponseStreamHandler {
	cache: Map<string, CacheValue> = new Map();

	getSink(operationKey: string): Sink<GraphQLResponse> | undefined {
		return this.cache.get(operationKey)?.sink;
	}

	getObservable(operationKey: string): Observable<GraphQLResponse> | undefined {
		return this.cache.get(operationKey)?.observable;
	}

	create(operationKey: string): Sink<GraphQLResponse> {
		if (this.cache.has(operationKey)) {
			throw new Error(`Operation ${operationKey} already exists in cache`);
		}

		const value: CacheValue = {
			buffered: [],
			closed: false,
			liveSubscribers: new Set(),
			// Filled in below — declared upfront so the closures in `sink` capture `value`.
			sink: null as unknown as Sink<GraphQLResponse>,
			observable: null as unknown as Observable<GraphQLResponse>,
		};

		value.sink = {
			next: (response: GraphQLResponse) => {
				if (value.closed) return;
				value.buffered.push({ kind: 'next', value: response });
				value.liveSubscribers.forEach((s) => {
					try {
						s.next(response);
					} catch {
						// Isolate: one bad subscriber must not starve others. The subscriber's own observable
						// chain will surface the error; we just keep multicast delivery going.
					}
				});
			},
			error: (error: Error) => {
				if (value.closed) return;
				value.closed = true;
				value.buffered.push({ kind: 'error', error });
				value.liveSubscribers.forEach((s) => {
					try {
						s.error(error);
					} catch {
						/* see note in next */
					}
				});
				// Drop references — buffered replay handles any future subscribers.
				value.liveSubscribers.clear();
			},
			complete: () => {
				if (value.closed) return;
				value.closed = true;
				value.buffered.push({ kind: 'complete' });
				value.liveSubscribers.forEach((s) => {
					try {
						s.complete();
					} catch {
						/* see note in next */
					}
				});
				// Drop references — buffered replay handles any future subscribers.
				value.liveSubscribers.clear();
			},
			closed: false,
		};

		value.observable = Observable.create<GraphQLResponse>((innerSink) => {
			// 1. Replay everything buffered so far.
			for (const event of value.buffered) {
				if (innerSink.closed) return;
				if (event.kind === 'next') innerSink.next(event.value);
				else if (event.kind === 'error') innerSink.error(event.error);
				else innerSink.complete();
			}

			// 2. If producer already finished, we're done. No need to track this sink.
			if (value.closed) return;

			// 3. Producer is still streaming — register for future events.
			value.liveSubscribers.add(innerSink);
			return () => value.liveSubscribers.delete(innerSink);
		});

		this.cache.set(operationKey, value);
		return value.sink;
	}
}

// FIXME: We really, really don't like initialisation at the module level.
const RelayPreloadedResponseStream: RelayPreloadedResponseStreamHandler =
	new RelayPreloadedResponseStreamHandler();

/**
 * Server-side handoff queue.
 *
 * The server's only handoff to the client is the HTML response itself — it
 * writes inline `<script>` blobs that push entries onto a `globalThis` queue.
 * This module owns that queue: on module init it drains any entries the
 * server already pushed (script tags ran before the bundle parsed), and it
 * replaces the queue with a live forwarder so any later script tags push
 * straight into the cache.
 */
type PreloadQueueEntry = {
	operationKey: string;
	response: GraphQLResponse;
	// Friendly name for diagnostics (perf marks, error logs). Server-emitted;
	// safe to read in instrumentation but not load-bearing for cache lookups.
	queryName?: string;
};

declare global {
	// eslint-disable-next-line no-var
	var __TOWNSQUARE_RELAY_PRELOAD_QUEUE__:
		| PreloadQueueEntry[]
		| { push(entry: PreloadQueueEntry): void }
		| undefined;
}

function ingest(entry: PreloadQueueEntry): void {
	if (RelayPreloadedResponseStream.cache.has(entry.operationKey)) {
		return;
	}
	const sink = RelayPreloadedResponseStream.create(entry.operationKey);
	sink.next(entry.response);
	sink.complete();
}

const existing = globalThis.__TOWNSQUARE_RELAY_PRELOAD_QUEUE__;
if (Array.isArray(existing)) {
	for (const entry of existing) {
		ingest(entry);
	}
}
globalThis.__TOWNSQUARE_RELAY_PRELOAD_QUEUE__ = { push: ingest };

export default RelayPreloadedResponseStream;
